import React, { useEffect, useRef, useState } from "react";
import { base44 } from "@/lib/api";
import { Phone, Mail, MapPin, Clock, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || "";

function TurnstileWidget({ onToken }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;
    if (!window.turnstile) {
      const s = document.createElement("script");
      s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
      s.async = true;
      s.defer = true;
      document.head.appendChild(s);
      const t = setInterval(() => {
        if (window.turnstile && ref.current && !ref.current.dataset.rendered) {
          window.turnstile.render(ref.current, { sitekey: TURNSTILE_SITE_KEY, callback: onToken });
          ref.current.dataset.rendered = "1";
          clearInterval(t);
        }
      }, 100);
      return () => clearInterval(t);
    }
    if (ref.current && !ref.current.dataset.rendered) {
      window.turnstile.render(ref.current, { sitekey: TURNSTILE_SITE_KEY, callback: onToken });
      ref.current.dataset.rendered = "1";
    }
  }, [onToken]);
  if (!TURNSTILE_SITE_KEY) return null;
  return <div ref={ref} className="cf-turnstile" />;
}

function ContactFAQs() {
  const [openIdx, setOpenIdx] = useState(null);
  return (
    <div className="bg-card border border-border rounded-xl p-7">
      <h3 className="font-heading text-lg font-bold text-foreground mb-2">Common Questions</h3>
      <div className="mt-2">
        {FAQS.map((faq, i) => (
          <div key={i} className="border-b border-border last:border-0">
            <button className="w-full flex items-center justify-between py-4 text-left" onClick={() => setOpenIdx(openIdx === i ? null : i)}>
              <span className="font-body text-sm font-semibold text-foreground pr-3">{faq.q}</span>
              <span className="text-muted-foreground text-lg leading-none flex-shrink-0">{openIdx === i ? "−" : "+"}</span>
            </button>
            {openIdx === i && <p className="font-body text-sm text-muted-foreground pb-4 leading-relaxed">{faq.a}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

const SERVICE_OPTIONS = [
  { value: "kitchen", label: "Kitchen Remodeling" },
  { value: "bathroom", label: "Bathroom Remodeling" },
  { value: "basement", label: "Basement Finishing" },
  { value: "whole-house", label: "Whole-House Renovation" },
  { value: "exterior", label: "Exterior Remodeling" },
  { value: "adu", label: "ADU & In-Law Suites" },
  { value: "outdoor-living", label: "Outdoor Living" },
  { value: "historic-restoration", label: "Historic Restoration" },
  { value: "aging-in-place", label: "Aging-in-Place" },
  { value: "other", label: "Other / Not Sure" },
];

const FAQS = [
  { q: "How quickly can you start?", a: "Most projects are scheduled 3–6 weeks out. For urgent repairs or smaller projects, we can sometimes accommodate sooner. We'll give you an honest timeline during the consultation." },
  { q: "Do you offer free estimates?", a: "Yes. Every estimate includes a free in-home consultation where we walk the space, discuss your goals, and provide a fixed-price quote with no obligation." },
  { q: "What areas do you serve?", a: "We serve the Greater Baltimore area — including Baltimore City, Baltimore County, Towson, Catonsville, Essex, Parkville, Owings Mills, and more." },
  { q: "Are you licensed and insured?", a: "Yes. MHIC# 05-147079 (Maryland) and PA HIC# PA177445 (Pennsylvania). Fully insured and bonded." },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service_interest: "", project_description: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [turnstileToken, setTurnstileToken] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      setError("Please complete the verification challenge.");
      return;
    }
    setLoading(true);
    try {
      await base44.entities.ContactSubmissions.create({ ...form, turnstile_token: turnstileToken });
      setSubmitted(true);
    } catch (err) {
      setError(err?.data?.error === "turnstile-failed"
        ? "Verification failed. Please refresh and try again."
        : "Something went wrong. Please call (410) 598-9933 or try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Hero */}
      <section className="bg-primary py-24 px-4">
        <div className="max-w-7xl mx-auto">
          <p className="font-body text-accent text-sm font-semibold uppercase tracking-[0.18em] mb-4">Get in Touch</p>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white max-w-2xl leading-tight">
            Free In-Home Consultation
          </h1>
          <p className="font-body text-white/65 mt-4 max-w-xl text-lg">
            No pressure, no obligation. We walk your space, listen to your goals, and give you a straight quote.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14">
          {/* Form */}
          <div>
            {submitted ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-card border border-border rounded-2xl p-10 text-center"
              >
                <div className="w-16 h-16 bg-accent/15 rounded-full flex items-center justify-center mx-auto mb-5">
                  <CheckCircle className="w-8 h-8 text-accent" />
                </div>
                <h2 className="font-heading text-2xl font-bold text-foreground mb-3">We'll Be in Touch Soon</h2>
                <p className="font-body text-muted-foreground mb-6">
                  Thank you, {form.name}. Dominic or the team typically reaches out within 1 business day to schedule your free consultation.
                </p>
                <p className="font-body text-sm text-muted-foreground">
                  Need to reach us sooner? Call{" "}
                  <a href="tel:4105989933" className="text-accent font-semibold">(410) 598-9933</a>
                </p>
              </motion.div>
            ) : (
              <div className="bg-card border border-border rounded-2xl p-8 shadow-sm">
                <h2 className="font-heading text-2xl font-bold text-foreground mb-6">Request a Free Estimate</h2>
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block font-body text-sm font-medium text-foreground mb-1.5">Full Name *</label>
                      <input
                        required
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="w-full border border-input rounded-lg px-4 py-2.5 font-body text-sm bg-background focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent transition-colors"
                        placeholder="Jane Smith"
                      />
                    </div>
                    <div>
                      <label className="block font-body text-sm font-medium text-foreground mb-1.5">Email *</label>
                      <input
                        type="email"
                        required
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className="w-full border border-input rounded-lg px-4 py-2.5 font-body text-sm bg-background focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent transition-colors"
                        placeholder="jane@example.com"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block font-body text-sm font-medium text-foreground mb-1.5">Phone</label>
                    <input
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="w-full border border-input rounded-lg px-4 py-2.5 font-body text-sm bg-background focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent transition-colors"
                      placeholder="(410) 555-0000"
                    />
                  </div>
                  <div>
                    <label className="block font-body text-sm font-medium text-foreground mb-1.5">Service Interest</label>
                    <select
                      value={form.service_interest}
                      onChange={(e) => setForm({ ...form, service_interest: e.target.value })}
                      className="w-full border border-input rounded-lg px-4 py-2.5 font-body text-sm bg-background focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent transition-colors"
                    >
                      <option value="">Select a service...</option>
                      {SERVICE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block font-body text-sm font-medium text-foreground mb-1.5">Tell us about your project</label>
                    <textarea
                      rows={4}
                      value={form.project_description}
                      onChange={(e) => setForm({ ...form, project_description: e.target.value })}
                      className="w-full border border-input rounded-lg px-4 py-2.5 font-body text-sm bg-background focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent transition-colors resize-none"
                      placeholder="What are you looking to do? Rough timeline? Any special considerations?"
                    />
                  </div>
                  {TURNSTILE_SITE_KEY && (
                    <TurnstileWidget onToken={setTurnstileToken} />
                  )}
                  {error && (
                    <p className="font-body text-sm text-destructive">{error}</p>
                  )}
                  <Button type="submit" disabled={loading} className="w-full bg-accent text-white hover:bg-accent/90 font-body font-semibold py-3 text-base">
                    {loading ? "Sending..." : "Request Free Estimate"}
                  </Button>
                  <p className="font-body text-xs text-muted-foreground text-center">
                    MHIC# 05-147079 · Licensed & Insured · We typically respond within 1 business day
                  </p>
                </form>
              </div>
            )}
          </div>

          {/* Info */}
          <div className="space-y-6">
            {/* Contact details */}
            <div className="bg-card border border-border rounded-xl p-7">
              <h3 className="font-heading text-xl font-bold text-foreground mb-5">Contact Information</h3>
              <div className="space-y-4">
                {[
                  { icon: Phone, label: "Phone", value: "(410) 598-9933", href: "tel:4105989933" },
                  { icon: Mail, label: "Email", value: "contact@topnotch-contractors.com", href: "mailto:contact@topnotch-contractors.com" },
                  { icon: MapPin, label: "Based in", value: "Dundalk, MD — Serving Baltimore & Surrounding Areas" },
                  { icon: Clock, label: "Hours", value: "Mon–Fri 7am–5pm · Sat by appointment" },
                ].map(({ icon: Icon, label, value, href }) => (
                  <div key={label} className="flex items-start gap-4">
                    <div className="w-9 h-9 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon className="w-4 h-4 text-accent" />
                    </div>
                    <div>
                      <p className="font-body text-xs text-muted-foreground mb-0.5">{label}</p>
                      {href ? (
                        <a href={href} className="font-body text-sm font-medium text-foreground hover:text-accent transition-colors">{value}</a>
                      ) : (
                        <p className="font-body text-sm font-medium text-foreground">{value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Trust */}
            <div className="bg-primary rounded-xl p-6 text-white">
              <h3 className="font-heading text-lg font-bold mb-4">Licensed & Insured</h3>
              <div className="space-y-2 font-body text-sm text-white/75">
                <p>MHIC# 05-147079 (Maryland)</p>
                <p>PA HIC# PA177445 (Pennsylvania)</p>
                <p>Fully insured and bonded</p>
                <p>500+ projects since 2010</p>
              </div>
            </div>

            {/* FAQs */}
            <ContactFAQs />
          </div>
        </div>
      </div>
    </div>
  );
}